import React, { useEffect, useRef, useState } from 'react';
import { Badge } from './ui/badge';
import { CheckCircle, BookOpen, Users, Award, ChevronLeft, ChevronRight } from 'lucide-react';

// =============================================
// ABOUT SECTION SLIDESHOW IMAGES
// Add or replace image paths here (total 9 slots)
// Place your image files in the /public folder
// then replace null with the path e.g. '/about-3.jpg'
// =============================================
const aboutImages = [
  '/about-1.png',   // Image 1 - uploaded
  '/about-2.jpg',   // Image 2 - uploaded
  '/about-3.jpg',             // Image 3 - add later
  '/about-4.jpg',             // Image 4 - add later
  '/about-5.jpg',           // Image 5 - add later
  '/about-6.jpg',             // Image 6 - add later
  '/about-7.jpg',             // Image 7 - add later
  '/about-8.jpg',             // Image 8 - add later
  '/about-9.jpg',             // Image 9 - add later
].filter(Boolean);  // removes empty slots automatically

export const About = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Auto-advance slideshow every 4 seconds
  useEffect(() => {
    if (aboutImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % aboutImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const prevSlide = () => setCurrentSlide(prev => (prev - 1 + aboutImages.length) % aboutImages.length);
  const nextSlide = () => setCurrentSlide(prev => (prev + 1) % aboutImages.length);

  const highlights = [
    { icon: BookOpen, text: "Comprehensive curriculum for all boards" },
    { icon: Users, text: "Small batch sizes for individual attention" },
    { icon: Award, text: "Proven track record of academic excellence" },
    { icon: CheckCircle, text: "Regular assessments and progress tracking" }
  ];

  return (
    <section id="about" ref={ref} className="relative py-24 lg:py-32 bg-slate-50">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #0d9488 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">

          {/* Image Slideshow side */}
          <div className={`relative scroll-fade-in ${isVisible ? 'is-visible' : ''}`}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">

              {/* Slides */}
              {aboutImages.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`ENIAC Coaching Centre ${index + 1}`}
                  className="w-full h-[450px] object-cover transition-opacity duration-700 absolute top-0 left-0"
                  style={{ opacity: index === currentSlide ? 1 : 0 }}
                />
              ))}
              {/* First image visible by default (not absolute) */}
              <img
                src={aboutImages[0]}
                alt="ENIAC Coaching Centre"
                className="w-full h-[450px] object-cover invisible"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />

              {/* Prev / Next buttons */}
              {aboutImages.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-md z-10"
                  >
                    <ChevronLeft className="w-5 h-5 text-slate-700" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-md z-10"
                  >
                    <ChevronRight className="w-5 h-5 text-slate-700" />
                  </button>
                </>
              )}

              {/* Dot indicators */}
              {aboutImages.length > 1 && (
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
                  {aboutImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'bg-white w-6 h-2'
                          : 'bg-white/50 w-2 h-2 hover:bg-white/80'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Floating card */}
            <div className="absolute -bottom-6 -right-4 lg:-right-8 bg-white rounded-2xl p-5 shadow-xl shadow-slate-900/10 border border-slate-100 max-w-[220px]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">25+</p>
                  <p className="text-sm text-slate-500">Years of Trust</p>
                </div>
              </div>
            </div>

            {/* Decorative element */}
            <div className="absolute -top-4 -left-4 w-24 h-24 rounded-2xl border-2 border-teal-200/50 -z-10" />
          </div>

          {/* Content side */}
          <div className={`scroll-fade-in ${isVisible ? 'is-visible' : ''}`} style={{ transitionDelay: '200ms' }}>
            <Badge className="bg-teal-50 text-teal-700 border-teal-200 px-4 py-1.5 text-sm font-medium mb-6">
              About Us
            </Badge>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
              Nurturing Minds,{' '}
              <span className="text-teal-600">Building Futures</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              Founded in Bardhaman, <strong className="text-slate-800">ENIAC Coaching Centre</strong> has been a beacon of quality education, 
              guiding students across CBSE, ICSE, and WBBSE boards toward academic excellence.
            </p>

            <p className="text-base text-slate-500 leading-relaxed mb-8">
              Our team of dedicated and experienced educators believes in fostering not just academic knowledge, 
              but also critical thinking, confidence, and a love for learning that stays with students throughout their lives.
            </p>

            <div className="space-y-4">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-white hover:shadow-sm transition-all duration-300 group"
                >
                  <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-100 transition-colors duration-300">
                    <item.icon className="w-5 h-5 text-teal-600" />
                  </div>
                  <span className="text-base font-medium text-slate-700">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
